import { useEffect, useState } from "react";
import type { ConnectionMetrics } from "../game/types";
import { BrandMark } from "./BrandMark";

export type LobbyPhase = "creating" | "connecting" | "waiting" | "opponent-ready" | "error";

interface LobbyScreenProps {
  phase: LobbyPhase;
  mode: "quick" | "private" | "join";
  joinCode: string | null;
  matchId: string | null;
  metrics: ConnectionMetrics;
  error: string | null;
  onCancel: () => void;
  onRetry: () => void;
}

export function LobbyScreen({ phase, mode, joinCode, matchId, metrics, error, onCancel, onRetry }: LobbyScreenProps) {
  const [elapsed, setElapsed] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1_000)), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  const copyCode = async () => {
    if (!joinCode) return;
    await navigator.clipboard.writeText(joinCode);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  };

  const title =
    phase === "creating"
      ? "Opening the arena"
      : phase === "connecting"
        ? "Connecting securely"
        : phase === "opponent-ready"
          ? "Challenger connected"
          : phase === "error"
            ? "Connection stopped"
            : mode === "join"
              ? "Waiting for the host"
              : "Waiting for a challenger";

  return (
    <main className="lobby-screen">
      <header className="lobby-header"><BrandMark compact /><span>Classic · First to 7</span></header>
      <section className="lobby-card" aria-live="polite">
        <div className={`matchmaking-orb${phase === "error" ? " matchmaking-orb--error" : ""}`} aria-hidden="true">
          <span /><span /><span /><i>VS</i>
        </div>
        <p className="eyebrow"><span /> {mode === "quick" ? "Public match" : "Private room"}</p>
        <h1>{title}</h1>
        <p className="lobby-card__status">
          {phase === "error"
            ? error
            : phase === "opponent-ready"
              ? "Both clients are synchronizing with the match server."
              : joinCode
                ? "Share the code below. Tickets are issued separately to each player."
                : "Requesting a temporary match ticket from the configured server."}
        </p>

        {joinCode && phase !== "error" && (
          <button className="join-code" type="button" onClick={() => void copyCode()} aria-label={`Copy room code ${joinCode}`}>
            <small>Room code</small>
            <strong>{joinCode}</strong>
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
        )}

        {phase === "error" ? (
          <button className="primary-button primary-button--small" type="button" onClick={onRetry}>Try again</button>
        ) : (
          <div className="lobby-progress"><i /><i /><i /><span>{elapsed}s</span></div>
        )}

        <dl className="connection-facts">
          <div><dt>Connection</dt><dd>{formatConnection(metrics.status)}</dd></div>
          <div><dt>Ping</dt><dd>{metrics.pingMs === null ? "—" : `${metrics.pingMs} ms`}</dd></div>
          <div><dt>Match</dt><dd>{matchId ? `${matchId.slice(0, 8)}…` : "Pending"}</dd></div>
        </dl>
        <button className="text-button" type="button" onClick={onCancel}>Cancel and return home</button>
      </section>
    </main>
  );
}

function formatConnection(status: ConnectionMetrics["status"]): string {
  if (status === "open") return "Connected";
  if (status === "reconnecting") return "Reconnecting";
  if (status === "connecting") return "Connecting";
  if (status === "error") return "Error";
  return "Offline";
}
